import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * @typedef {object} SizePickerOpenOptions
 * @property {string} productUrl
 * @property {'add' | 'confirm'} [mode]
 * @property {(variantId: string) => void} [onConfirm]
 */

/**
 * Compact size-picker dialog for off-PDP add-to-cart flows.
 */
export class SizePickerDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {'add' | 'confirm'} */
  #mode = 'add';

  /** @type {((variantId: string) => void) | null} */
  #onConfirm = null;

  /** @type {boolean} */
  #hasUserSelection = false;

  /** @type {string | null} */
  #productUrl = null;

  /** @type {{ top: string, position: string, width: string } | null} */
  #preserveCartLock = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected);
    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClosed);
    // Capture so we run before parent <a> navigation and before any bubble stopPropagation.
    document.addEventListener('click', this.#handleTriggerClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    document.removeEventListener('click', this.#handleTriggerClick, true);
  }

  /**
   * Opens the size picker for a product URL.
   * @param {SizePickerOpenOptions} options
   */
  async open(options) {
    const { productUrl, mode = 'add', onConfirm = null } = options;

    if (!productUrl) return;

    this.#mode = mode;
    this.#onConfirm = onConfirm;
    this.#hasUserSelection = false;
    this.#productUrl = productUrl.split('?')[0];

    const cartOpen = document.querySelector(
      'cart-drawer-component dialog[open]',
    );
    this.#preserveCartLock =
      cartOpen instanceof HTMLDialogElement
        ? {
            top: document.body.style.top,
            position: document.body.style.position,
            width: document.body.style.width,
          }
        : null;

    try {
      const loaded = await this.#loadContent(this.#productUrl);
      if (!loaded) return;
    } catch (error) {
      // Superseded by a newer open() call — ignore.
      if (error instanceof DOMException && error.name === 'AbortError') return;
      throw error;
    }

    this.showDialog();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-size-picker-open]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const productUrl = trigger.dataset.sizePickerOpen;
    if (!productUrl) return;

    const mode = trigger.dataset.sizePickerMode === 'confirm' ? 'confirm' : 'add';

    void this.open({ productUrl, mode });
  };

  /**
   * @param {string} productUrl
   * @returns {Promise<boolean>} true when content was loaded for this request
   */
  async #loadContent(productUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const sectionUrl = `${productUrl}?section_id=size-picker`;
    const response = await fetch(sectionUrl, { signal });

    if (!response.ok) {
      throw new Error(`Failed to load size picker: HTTP ${response.status}`);
    }

    const responseText = await response.text();
    if (signal.aborted) return false;

    const html = new DOMParser().parseFromString(responseText, 'text/html');
    const sectionContent =
      html.querySelector('[data-size-picker-section]') ||
      html.querySelector('.modal.size-picker') ||
      html.querySelector('.size-picker') ||
      html.querySelector('.shopify-section')?.firstElementChild;

    const content = document.getElementById('size-picker-modal-content');
    if (!content || !sectionContent) return false;

    content.replaceChildren();
    content.appendChild(document.importNode(sectionContent, true));

    this.#prepareContent();
    return true;
  }

  #prepareContent() {
    const root =
      this.querySelector('[data-size-picker-section]') ||
      this.querySelector('.modal.size-picker') ||
      this.querySelector('.size-picker');
    if (!(root instanceof HTMLElement)) return;

    root.dataset.mode = this.#mode;

    this.#resetSizeSelection(root);
    this.#syncAddButtonState();
    this.#bindFormIntercept();
    this.#updateConfirmLabel();
  }

  /**
   * Clears pre-selected size options so the shopper must choose.
   * @param {HTMLElement} root
   */
  #resetSizeSelection(root) {
    const optionGroups = root.querySelectorAll('.variant-option--buttons');

    for (const fieldset of optionGroups) {
      const isSizeFieldset =
        fieldset.querySelector(
          '[data-size-system], [data-size-label], .variant-option__size-tabs',
        ) != null;

      // Keep non-size options (e.g. color) selected when multiple groups exist.
      if (optionGroups.length > 1 && !isSizeFieldset) continue;

      for (const input of fieldset.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
        }
      }
    }

    const variantInput = root.querySelector('[data-size-picker-variant-id]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = '';
      variantInput.disabled = true;
    }

    this.#hasUserSelection = false;
  }

  #bindFormIntercept() {
    const formComponent = this.querySelector('product-form-component');
    const form = formComponent?.querySelector('form');
    const addButton = this.querySelector('[data-size-picker-add], .add-btn');

    if (form instanceof HTMLFormElement) {
      form.addEventListener('submit', (event) => event.preventDefault());
    }

    if (addButton instanceof HTMLButtonElement) {
      addButton.addEventListener('click', this.#handleAddClick);
    }
  }

  /**
   * Resolves the currently selected available variant id, or null.
   * @returns {string | null}
   */
  #resolveVariantId() {
    const selected = this.#getSelectedAvailableOption();
    const variantInput = this.querySelector('[data-size-picker-variant-id]');
    const variantId =
      variantInput instanceof HTMLInputElement &&
      variantInput.value &&
      !variantInput.disabled
        ? variantInput.value
        : selected instanceof HTMLInputElement
          ? selected.dataset.variantId
          : null;

    if (!this.#hasUserSelection || !selected || !variantId) return null;
    return String(variantId);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleAddClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const hint = this.querySelector('[data-size-picker-hint]');
    const variantId = this.#resolveVariantId();

    if (!variantId) {
      hint?.classList.add('is-visible');
      hint?.removeAttribute('hidden');
      this.#syncAddButtonState();
      return;
    }

    hint?.classList.remove('is-visible');
    hint?.setAttribute('hidden', '');

    // Tilbehør / Tilkøb: only save the chosen variant — do not add to cart here.
    if (this.#mode === 'confirm') {
      if (this.#onConfirm) {
        this.#onConfirm(variantId);
      }
      void this.closeDialog();
      return;
    }

    // Collections / cart drawer: add to cart via product-form.
    const formComponent = this.querySelector('product-form-component');
    if (
      formComponent &&
      typeof /** @type {{ handleSubmit?: Function }} */ (formComponent)
        .handleSubmit === 'function'
    ) {
      /** @type {{ handleSubmit: Function }} */ (formComponent).handleSubmit(
        event,
      );
    }
  };

  #onVariantSelected = () => {
    this.#hasUserSelection = true;
    const hint = this.querySelector('[data-size-picker-hint]');
    hint?.classList.remove('is-visible');
    hint?.setAttribute('hidden', '');
  };

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    this.#hasUserSelection = true;

    const resource = event.detail?.resource;
    const imageContainer = this.querySelector('[data-size-picker-image]');
    const previewSrc = resource?.featured_media?.preview_image?.src;

    if (imageContainer instanceof HTMLElement && previewSrc) {
      const img = imageContainer.querySelector('img');
      if (img instanceof HTMLImageElement) {
        img.src = `${previewSrc}&width=112`;
      }
    }

    const variantInput = this.querySelector('[data-size-picker-variant-id]');
    if (variantInput instanceof HTMLInputElement && resource?.id) {
      variantInput.value = String(resource.id);
      variantInput.disabled = resource.available === false;
    }

    this.#syncAddButtonState();
  };

  #syncAddButtonState() {
    const button = this.querySelector('.add-btn');
    if (!(button instanceof HTMLButtonElement)) return;

    const selected = this.#getSelectedAvailableOption();
    const hasSelection = this.#hasUserSelection && selected != null;

    button.classList.toggle('is-disabled', !hasSelection);
    button.setAttribute('aria-disabled', String(!hasSelection));

    // Stay clickable without a selection so the hint can appear; hard-disable only when OOS.
    button.disabled = Boolean(
      hasSelection && selected?.dataset.optionAvailable === 'false',
    );
  }

  #updateConfirmLabel() {
    if (this.#mode !== 'confirm') return;

    const label = this.querySelector('.add-btn__label');
    if (!(label instanceof HTMLElement)) return;

    label.textContent = this.dataset.confirmLabel || 'Confirm size';
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getSelectedAvailableOption() {
    const fieldsets = this.querySelectorAll('.variant-option--buttons');

    if (fieldsets.length > 0) {
      for (const fieldset of fieldsets) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!(checked instanceof HTMLInputElement)) return null;
        if (checked.dataset.optionAvailable === 'false') return null;
      }

      const withVariantId = this.querySelector(
        '.variant-option--buttons input[type="radio"]:checked[data-variant-id]',
      );
      if (withVariantId instanceof HTMLInputElement) return withVariantId;

      const anyChecked = this.querySelector(
        '.variant-option--buttons input[type="radio"]:checked',
      );
      return anyChecked instanceof HTMLInputElement ? anyChecked : null;
    }

    const checked = this.querySelector(
      '.variant-option input[type="radio"]:checked, .variant-option__select option[selected]',
    );
    if (
      !(checked instanceof HTMLInputElement) &&
      !(checked instanceof HTMLOptionElement)
    ) {
      return null;
    }
    if (
      checked instanceof HTMLInputElement &&
      checked.dataset.optionAvailable === 'false'
    ) {
      return null;
    }
    return checked instanceof HTMLInputElement ? checked : null;
  }

  /**
   * @param {CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;
    if (this.#mode !== 'add') return;

    // Refresh cart line items before closing. Closing the nested dialog can race
    // with the header morph when adding from cart-drawer recommendations.
    void this.#refreshCartItems(event.detail?.data?.sections).finally(() => {
      void this.closeDialog();
    });
  };

  /**
   * Ensures cart-items-component(s) morph to the latest cart HTML.
   * @param {Record<string, string> | undefined} sections
   */
  async #refreshCartItems(sections) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    for (const component of cartItemsComponents) {
      if (!(component instanceof HTMLElement) || !component.dataset.sectionId) {
        continue;
      }

      const sectionId = component.dataset.sectionId;
      const sectionHtml = sections?.[sectionId];

      try {
        if (typeof sectionHtml === 'string' && sectionHtml.length > 0) {
          await morphSection(sectionId, sectionHtml);
        } else {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        }
      } catch (error) {
        console.error('[size-picker] cart refresh failed', error);
        try {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        } catch (fallbackError) {
          console.error('[size-picker] cart refresh fallback failed', fallbackError);
        }
      }
    }
  }

  #onDialogClosed = (event) => {
    if (event.target !== this) return;

    if (this.#preserveCartLock) {
      document.body.style.position =
        this.#preserveCartLock.position || 'fixed';
      document.body.style.top = this.#preserveCartLock.top;
      document.body.style.width = this.#preserveCartLock.width || '100%';
      document.documentElement.setAttribute('scroll-lock', '');
      this.#preserveCartLock = null;
    }

    this.#onConfirm = null;
    this.#hasUserSelection = false;
  };
}

if (!customElements.get('size-picker-dialog')) {
  customElements.define('size-picker-dialog', SizePickerDialog);
}

/**
 * Helper used by other modules.
 * @param {SizePickerOpenOptions} options
 */
export async function openSizePicker(options) {
  const dialog = document.getElementById('size-picker-dialog');
  if (!(dialog instanceof SizePickerDialog)) return;
  await dialog.open(options);
}
